import { useIsLoggedIn } from '@/hooks/auth';
import { useSettings } from '@/hooks/state/useSettings';
import { deleteApiV1BoardsId, patchApiV1BoardsId, postApiV1Boards, useGetApiV1BoardsAssets } from '@/open-api/boards/boards';
import type { BoardAssetType, GetBoardAllAssetsItemBoardResponse, GetBoardAllAssetsItemResponse, GetBoardsItemResponse, NullableOfBoardSortOrder, PatchBoardRequestBody } from '@/open-api/model';
import { logEvent } from '@/pages/api/v1/utils';
import type { AssetGroup } from '@/types/asset';
import type { Track } from '@/types/tracks';
import { boardsKeys } from '@/utils/queryKeys';
import { toast } from '@music-vine/cadence/ui';
import { createContext, type SetStateAction, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { BOARDS_POPUP_CLOSE_DELAY_MS, NEW_BOARD_PENDING_ID } from './constants';
import { useInfiniteGetBoards, type useInfiniteGetBoardsReturnType } from './hooks/useGetBoardsInfinite';
import { useBoardAssetOperations } from './hooks/useBoardAssetOperations';
import type { AssetBoardOperationProps, Board, BoardAssetId, BoardAssetIdentifiers, BoardsContextVisibleElement, CloseBoardsPopupParams, NewlyCreatedBoard } from './models';
import { getBoardAssetTypeFromAssetGroup } from './utils/get-board-asset-type-from-asset-group';
import { getFavoritesBoardId } from './utils/get-favorites-board-id';
import { isPendingBoardId } from './utils/is-pending-board-id';
export const BOARD_OPERATION_DEBOUNCE_DELAY_MS = 300 as const;
export const BOARD_ASSET_REF_REMOVAL_DELAY_MS = 200 as const;
export type CreateBoardMode = 'create_board_with_asset' | 'create_board_only';
interface OpenBoardsPopupProps {
  asset: AssetGroup;
  isFavorite?: boolean;
  openPopoverInActionMenu?: boolean;
}
interface CreateBoardProps {
  asset?: BoardAssetIdentifiers;
  name: string;
  nextElement?: BoardsContextVisibleElement | null;
}
interface RenameBoardNameParams {
  boardId: number;
  currentName: string;
  newName: string;
}
type BoardsToastId = string | number | null;
export interface BoardsContextType {
  /* CRUD operations */
  addAssetToBoard: (params: AssetBoardOperationProps) => void;
  addAssetToFavoritesBoard: (params: Omit<AssetBoardOperationProps, 'boardId'>) => void;
  /* Values */
  asset: AssetGroup | null;
  assetExistsInAnyBoard: ({
    assetId,
    assetType
  }: {
    assetId: BoardAssetId;
    assetType: BoardAssetType;
  }) => boolean;

  /* Helpers */
  assetExistsInBoard: (params: AssetBoardOperationProps) => boolean;
  boardAssets: GetBoardAllAssetsItemResponse[];
  boardSortOrder: NullableOfBoardSortOrder;
  boards: GetBoardsItemResponse[];
  boardsPopupIsOpen: boolean;
  boardsQueryResponse: useInfiniteGetBoardsReturnType;
  boardsState: BoardsState;
  closeBoardsDrawer: () => void;
  closeBoardsPopover: () => void;
  closeBoardsPopup: (params?: CloseBoardsPopupParams) => void;
  closeNewBoardDialog: () => void;
  createBoard: (params: CreateBoardProps) => Promise<void>;
  deleteBoard: (params: {
    boardId: number;
  }) => Promise<void>;
  dismissAllOpenToasts: () => void;
  drawerIsOpen: boolean;
  favoritesBoardId: number | null;
  getAssetBoards: ({
    id,
    type
  }: BoardAssetIdentifiers) => GetBoardAllAssetsItemBoardResponse[] | undefined;
  getBoard: ({
    id
  }: {
    id: number;
  }) => GetBoardsItemResponse | undefined;
  getFavoritesBoard: () => GetBoardsItemResponse | undefined;
  interruptBoardsPopupAutoBehaviour: (props?: {
    isDirty?: boolean;
  }) => void;
  newBoardDialogIsOpen: boolean;
  openBoardsDrawer: ({
    asset
  }: OpenBoardsPopupProps) => void;
  openBoardsPopover: ({
    asset
  }: OpenBoardsPopupProps) => void;
  openBoardsPopup: ({
    asset,
    isFavorite
  }: OpenBoardsPopupProps) => void;
  openNewBoardDialog: () => void;
  patchBoardInContext: ({
    id,
    data
  }: {
    id: number;
    data: NonNullable<PatchBoardRequestBody>;
  }) => void;
  registerOpenToastId: (toastId: BoardsToastId) => void;
  removeAssetFromBoard: (params: AssetBoardOperationProps) => void;
  renameBoard: (params: RenameBoardNameParams) => Promise<void>;
  setAsset: (asset: AssetGroup | null) => void;
  setBoardSortOrder: (value: NullableOfBoardSortOrder) => void;
  setBoardsPopupIsOpen: (isOpen: boolean) => void;
  setBoardsState: (value: SetStateAction<BoardsState>) => void;
}
export const defaultBoardsContextState: BoardsContextType = {
  asset: null,
  boards: [],
  boardAssets: [],
  boardsQueryResponse: {} as useInfiniteGetBoardsReturnType,
  boardsPopupIsOpen: false,
  boardsState: {
    isDirty: false
  },
  boardSortOrder: 'Latest',
  drawerIsOpen: false,
  favoritesBoardId: null,
  getBoard: () => null,
  getFavoritesBoard: () => null,
  newBoardDialogIsOpen: false,
  setAsset: () => null,
  setBoardsPopupIsOpen: () => null,
  openBoardsPopup: () => null,
  closeBoardsPopup: () => null,
  openBoardsPopover: () => null,
  closeBoardsPopover: () => null,
  setBoardsState: () => null,
  interruptBoardsPopupAutoBehaviour: () => null,
  openNewBoardDialog: () => null,
  closeNewBoardDialog: () => null,
  removeAssetFromBoard: () => null,
  addAssetToBoard: () => null,
  addAssetToFavoritesBoard: () => null,
  createBoard: () => Promise.resolve(),
  assetExistsInBoard: () => false,
  assetExistsInAnyBoard: () => false,
  openBoardsDrawer: () => null,
  closeBoardsDrawer: () => null,
  dismissAllOpenToasts: () => null,
  registerOpenToastId: () => null,
  renameBoard: () => Promise.resolve(),
  deleteBoard: () => Promise.resolve(),
  setBoardSortOrder: () => null,
  getAssetBoards: () => [],
  patchBoardInContext: () => null
};
export const BoardsContext = createContext<BoardsContextType>(defaultBoardsContextState);
export interface BoardsState {
  isDirty: boolean;
  visibleElement?: BoardsContextVisibleElement | null;
}
export interface Favorite {
  asset: {
    id: Track['id'];
  };
  boards: {
    id: Board['id'];
  }[];
}
export const BoardsContextProvider = ({
  children
}: {
  children: React.ReactNode;
}) => {
  const [boardSortOrder, setBoardSortOrder] = useState<NullableOfBoardSortOrder>('Latest');
  const [boardAssets, setBoardAssets] = useState<GetBoardAllAssetsItemResponse[]>([]);
  const [boardsPopupIsOpen, setBoardsPopupIsOpen] = useState<boolean>(false);
  const [asset, setAsset] = useState<AssetGroup | null>(null);
  const [boardsState, setBoardsState] = useState<BoardsState>({
    isDirty: false,
    visibleElement: null
  });
  const [newBoardDialogIsOpen, setNewBoardDialogIsOpen] = useState<boolean>(false);
  const openToastIdsRef = useRef<BoardsToastId[]>([]);
  const closeBoardsPopupTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingFavoriteRef = useRef<Omit<AssetBoardOperationProps, 'boardId'> | null>(null);
  const userIsLoggedIn = useIsLoggedIn();
  const {
    settings
  } = useSettings();
  const boardsQueryResponse = useInfiniteGetBoards({
    userId: settings?.user.id,
    boardSortOrder,
    userIsLoggedIn
  });
  const {
    data: boardAssetsResponse
  } = useGetApiV1BoardsAssets({
    query: {
      queryKey: boardsKeys.boardAssets(settings?.user.id),
      enabled: userIsLoggedIn,
      retry: failureCount => failureCount < 1
    }
  });
  const fetchedBoards = useMemo(() => boardsQueryResponse.boards || [], [boardsQueryResponse.boards]);

  // Local copy of the fetched boards so CRUD operations can update optimistically.
  // Synced during render rather than in an effect: an effect commits a render where
  // the query has resolved but `boards` is still empty, which flashes
  // "Board not found" on the board pages (DEV-6417).
  // The identity check below relies on `fetchedBoards` being memoised — an
  // unstable reference would resync every render and clobber optimistic updates.
  const [boards, setBoards] = useState<GetBoardsItemResponse[]>(fetchedBoards);
  const [lastFetchedBoards, setLastFetchedBoards] = useState(fetchedBoards);
  if (lastFetchedBoards !== fetchedBoards) {
    setLastFetchedBoards(fetchedBoards);
    setBoards(fetchedBoards);
  }
  const favoritesBoardId = useMemo(() => getFavoritesBoardId({
    data: fetchedBoards
  }), [fetchedBoards]);
  const getBoard = useCallback(({
    id
  }: {
    id: number;
  }) => boards.find(board => board && board.id === id), [boards]);
  const getFavoritesBoard = useCallback(() => {
    if (!favoritesBoardId) {
      return;
    }
    return getBoard({
      id: favoritesBoardId
    });
  }, [getBoard, favoritesBoardId]);

  // Asset operations (add/remove with optimistic updates)
  const {
    assetExistsInBoard,
    assetExistsInAnyBoard,
    addAssetToBoard,
    addAssetToFavoritesBoard,
    addAssetToBoardContext,
    removeAssetFromBoard,
    removeAssetFromBoardContext
  } = useBoardAssetOperations({
    boards,
    boardAssets,
    setBoardAssets,
    favoritesBoardId
  });

  // Popup timeout management
  const clearBoardsPopupTimeout = useCallback(() => {
    if (closeBoardsPopupTimeoutRef.current) {
      clearTimeout(closeBoardsPopupTimeoutRef.current);
      closeBoardsPopupTimeoutRef.current = null;
    }
  }, []);
  const closeBoardsPopup = useCallback(({
    clearAssetContext
  }: CloseBoardsPopupParams = {
    clearAssetContext: true
  }) => {
    setBoardsPopupIsOpen(false);
    clearBoardsPopupTimeout();

    /**
     * Don't remove the asset instantly so the image and asset title doesn't
     * disappear when the board popup is closing (animation takes a few milliseconds)
     */
    if (clearAssetContext) {
      setTimeout(() => setAsset(null), BOARD_ASSET_REF_REMOVAL_DELAY_MS);
    }
    setBoardsState({
      isDirty: false,
      visibleElement: null
    });
  }, [clearBoardsPopupTimeout]);
  const interruptBoardsPopupAutoBehaviour = useCallback(({
    isDirty
  }: {
    isDirty?: boolean;
  } = {
    isDirty: false
  }) => {
    clearBoardsPopupTimeout();
    if (isDirty) {
      setBoardsState(prev => ({
        ...prev,
        isDirty: true
      }));
    }
  }, [clearBoardsPopupTimeout]);
  const openBoardsPopup = useCallback(({
    asset: assetData
  }: OpenBoardsPopupProps) => {
    setAsset(assetData);
    setBoardsPopupIsOpen(true);
    setBoardsState(prev => ({
      ...prev,
      visibleElement: 'popup'
    }));
    if (closeBoardsPopupTimeoutRef.current) {
      clearBoardsPopupTimeout();
    }
    const boardAssetType = getBoardAssetTypeFromAssetGroup(assetData);
    if (assetExistsInAnyBoard({
      assetId: Number(assetData.data.id),
      assetType: boardAssetType
    })) {
      return;
    }
    const favorite = {
      assetId: Number(assetData.data.id),
      assetType: boardAssetType
    };
    if (favoritesBoardId) {
      addAssetToBoard({
        ...favorite,
        boardId: favoritesBoardId
      });
    } else if (boardsQueryResponse.isPending) {
      // Boards are still loading: save once the Favorites board id is known.
      pendingFavoriteRef.current = favorite;
    } else {
      return;
    }
    closeBoardsPopupTimeoutRef.current = setTimeout(() => {
      closeBoardsPopup();
    }, BOARDS_POPUP_CLOSE_DELAY_MS);
  }, [closeBoardsPopup, addAssetToBoard, assetExistsInAnyBoard, clearBoardsPopupTimeout, favoritesBoardId, boardsQueryResponse.isPending]);
  useEffect(() => {
    const pending = pendingFavoriteRef.current;
    if (!(pending && favoritesBoardId)) {
      return;
    }
    pendingFavoriteRef.current = null;
    addAssetToBoard({
      ...pending,
      boardId: favoritesBoardId
    });
  }, [favoritesBoardId, addAssetToBoard]);
  const openNewBoardDialog = useCallback(() => {
    clearBoardsPopupTimeout();
    setNewBoardDialogIsOpen(true);
    setBoardsState(prev => ({
      ...prev,
      visibleElement: 'dialog'
    }));
  }, [clearBoardsPopupTimeout]);
  const closeNewBoardDialog = useCallback(() => {
    setNewBoardDialogIsOpen(false);
    setBoardsState(prev => ({
      ...prev,
      visibleElement: 'popup'
    }));
  }, []);

  // Board CRUD operations
  const createBoard = useCallback(async ({
    name,
    asset: assetDetails,
    nextElement
  }: CreateBoardProps) => {
    const newBoardPreRequest: NewlyCreatedBoard = {
      id: NEW_BOARD_PENDING_ID,
      name,
      isPinned: false,
      shareId: '',
      isPublic: false,
      assetImageUrls: []
    };
    const boardWithSameName = boards.find(board => (board?.name ?? '').trim() === name.trim());
    if (boardWithSameName) {
      toast.info('A board with this name already exists');
      return;
    }
    let newBoards = boards;
    const insertAtIndex = newBoards.findIndex(board => board && !board.isPinned);
    if (insertAtIndex > -1) {
      newBoards.splice(insertAtIndex, 0, newBoardPreRequest);
    } else {
      newBoards = [...newBoards, newBoardPreRequest];
    }
    setBoards(newBoards);
    try {
      if (assetDetails) {
        addAssetToBoardContext({
          assetId: assetDetails.id,
          assetType: assetDetails.type,
          boardId: newBoardPreRequest.id
        });
      }
      setBoardsState(prev => ({
        ...prev,
        isDirty: true
      }));
      setNewBoardDialogIsOpen(false);
      setBoardsState(prev => ({
        ...prev,
        visibleElement: nextElement ?? 'popup'
      }));
      const newBoardPostRequest = await postApiV1Boards({
        name,
        isPinned: false
      });
      if (newBoardPostRequest?.id) {
        setBoards(prev => prev.map(board => board && board.id === newBoardPreRequest.id && isPendingBoardId(board.id) ? {
          ...newBoardPostRequest,
          assetImageUrls: board.assetImageUrls
        } : board));
        void logEvent({
          event_type: 'create_board',
          asPath: typeof window === 'undefined' ? '' : window.location.pathname,
          metadata: {
            type: 'create',
            board_id: newBoardPostRequest.id,
            board_name: name
          }
        });
        if (assetDetails) {
          addAssetToBoard({
            assetId: assetDetails.id,
            assetType: assetDetails.type,
            boardId: newBoardPostRequest.id
          });
          removeAssetFromBoardContext({
            assetId: assetDetails.id,
            assetType: assetDetails.type,
            boardId: newBoardPreRequest.id
          });
        }
      }
    } catch {
      toast.error("Sorry, we couldn't create the board");
      if (assetDetails) {
        removeAssetFromBoard({
          assetId: assetDetails.id,
          assetType: assetDetails.type,
          boardId: newBoardPreRequest.id
        });
      }
      setBoards(boards.filter(board => board && board.id !== newBoardPreRequest.id));
    }
  }, [boards, removeAssetFromBoard, addAssetToBoard, addAssetToBoardContext, removeAssetFromBoardContext]);

  // Popover/drawer/toast state
  const openBoardsPopover = useCallback(({
    asset: assetData,
    openPopoverInActionMenu
  }: OpenBoardsPopupProps) => {
    const visibleElement = openPopoverInActionMenu ? 'popover_in_menu' : 'popover';
    setAsset(assetData);
    setBoardsState(prev => ({
      ...prev,
      visibleElement
    }));
  }, []);
  const closeBoardsPopover = useCallback(() => {
    setAsset(null);
    setBoardsState(prev => ({
      ...prev,
      isDirty: false,
      visibleElement: null
    }));
  }, []);
  const openBoardsDrawer = useCallback(({
    asset: assetData
  }: OpenBoardsPopupProps) => {
    setAsset(assetData);
    setBoardsState(prev => ({
      ...prev,
      isDirty: false,
      visibleElement: 'drawer'
    }));
  }, []);
  const closeBoardsDrawer = useCallback(() => {
    setAsset(null);
    setBoardsState(prev => ({
      ...prev,
      isDirty: false,
      visibleElement: null
    }));
  }, []);
  const registerOpenToastId = useCallback((toastId: string | number | null) => {
    openToastIdsRef.current.push(toastId);
  }, []);
  const dismissAllOpenToasts = useCallback(() => {
    openToastIdsRef.current.forEach(toastId => {
      if (toastId) {
        toast.dismiss(toastId);
      }
    });
    openToastIdsRef.current = [];
  }, []);

  // Board patch/rename/delete
  const patchBoardInContext = useCallback(({
    id,
    data: {
      name,
      isPinned,
      isPublic
    }
  }: {
    id: number;
    data: NonNullable<PatchBoardRequestBody>;
  }) => {
    setBoards(prev => prev.map(board => board && board.id === id ? {
      ...board,
      name: name || board.name,
      isPinned: typeof isPinned === 'boolean' ? isPinned : board.isPinned,
      isPublic: typeof isPublic === 'boolean' ? isPublic : board.isPublic
    } : board));
  }, []);
  const renameBoardInContext = useCallback(({
    boardId,
    newName
  }: Omit<RenameBoardNameParams, 'currentName'>) => {
    patchBoardInContext({
      id: boardId,
      data: {
        name: newName
      }
    });
  }, [patchBoardInContext]);
  const renameBoard = useCallback(async ({
    boardId,
    currentName: oldName,
    newName
  }: RenameBoardNameParams) => {
    try {
      await patchApiV1BoardsId(boardId, {
        name: newName
      });
      renameBoardInContext({
        boardId,
        newName
      });
      void logEvent({
        event_type: 'rename_board',
        asPath: typeof window === 'undefined' ? '' : window.location.pathname,
        metadata: {
          type: 'rename',
          board_id: boardId,
          old_board_name: oldName,
          new_board_name: newName
        }
      });
    } catch {
      toast.error("Sorry, we couldn't rename the board");
      renameBoardInContext({
        boardId,
        newName: oldName
      });
    }
  }, [renameBoardInContext]);
  const deleteBoardInContext = useCallback(({
    boardId
  }: {
    boardId: number;
  }) => {
    setBoards(prev => prev.filter(board => board && board.id !== boardId));
  }, []);
  const deleteBoard = useCallback(async ({
    boardId
  }: {
    boardId: number;
  }) => {
    const board = boards.find(b => b?.id === boardId);
    try {
      await deleteApiV1BoardsId(boardId);
      deleteBoardInContext({
        boardId
      });
      void logEvent({
        event_type: 'delete_board',
        asPath: typeof window === 'undefined' ? '' : window.location.pathname,
        metadata: {
          type: 'delete',
          board_id: boardId,
          board_name: board?.name
        }
      });
    } catch {
      toast.error("Sorry, we couldn't delete the board");
    }
  }, [deleteBoardInContext, boards]);
  const getAssetBoards = useCallback(({
    id,
    type
  }: BoardAssetIdentifiers) => boardAssets.find(a => a?.assetId === id && a?.assetType === type)?.boards, [boardAssets]);

  // Sync effects
  useEffect(() => {
    if (boardAssetsResponse) {
      setBoardAssets(boardAssetsResponse.assets);
    }
  }, [boardAssetsResponse]);
  useEffect(() => dismissAllOpenToasts, [dismissAllOpenToasts]);
  const value: BoardsContextType = useMemo(() => ({
    asset,
    boards,
    boardAssets,
    boardsQueryResponse,
    boardsPopupIsOpen,
    boardsState,
    boardSortOrder,
    drawerIsOpen: boardsState.visibleElement === 'drawer',
    newBoardDialogIsOpen,
    favoritesBoardId,
    setAsset,
    setBoardsPopupIsOpen,
    setBoardSortOrder,
    openBoardsPopup,
    closeBoardsPopup,
    setBoardsState,
    interruptBoardsPopupAutoBehaviour,
    openNewBoardDialog,
    closeNewBoardDialog,
    removeAssetFromBoard,
    addAssetToBoard,
    addAssetToFavoritesBoard,
    createBoard,
    assetExistsInBoard,
    assetExistsInAnyBoard,
    openBoardsPopover,
    closeBoardsPopover,
    openBoardsDrawer,
    closeBoardsDrawer,
    registerOpenToastId,
    dismissAllOpenToasts,
    renameBoard,
    deleteBoard,
    getBoard,
    getFavoritesBoard,
    getAssetBoards,
    patchBoardInContext
  }), [asset, boards, boardAssets, boardsState, boardsPopupIsOpen, boardSortOrder, newBoardDialogIsOpen, openBoardsPopup, closeBoardsPopup, interruptBoardsPopupAutoBehaviour, openNewBoardDialog, closeNewBoardDialog, removeAssetFromBoard, addAssetToBoard, addAssetToFavoritesBoard, createBoard, assetExistsInBoard, assetExistsInAnyBoard, openBoardsPopover, closeBoardsPopover, openBoardsDrawer, closeBoardsDrawer, dismissAllOpenToasts, registerOpenToastId, renameBoard, deleteBoard, getBoard, getFavoritesBoard, boardsQueryResponse, favoritesBoardId, getAssetBoards, patchBoardInContext]);
  return <BoardsContext value={value} data-sentry-element="BoardsContext" data-sentry-component="BoardsContextProvider" data-sentry-source-file="context-provider.tsx">{children}</BoardsContext>;
};
export const useBoardsContext = () => {
  const context = useContext(BoardsContext);
  if (!context) {
    throw new Error('useBoardsContext must be used within a BoardsContextProvider');
  }
  return context;
};